import React, { useState, useRef } from 'react';
import { rptApi } from '../services/api';
import '../styles/Panel.css';

function FetchGLPanel({ entity, onComplete }) {
  const [startDate, setStartDate] = useState('2024-04-01');
  const [endDate, setEndDate] = useState('2025-03-31');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Manual upload flow applies when the backend explicitly marks the entity as
  // upload-enabled. This includes MANUAL/N/A entities and Xero entities with
  // `manual_like_format: true` (Everlife Holding Pte. Ltd. / EH).
  const connectionType = entity?.connection_type?.toUpperCase();
  const isEverlifeXeroManualLike = entity?.finalyzer_company_code === 'EH'
    && connectionType === 'XERO'
    && entity?.sap_database === 'N/A';
  const isManualUploadEntity = Boolean(entity?.manual_gl_upload_enabled)
    || ['N/A', 'MANUAL'].includes(connectionType)
    || isEverlifeXeroManualLike;

  const handleFetchGL = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await rptApi.fetchGL(entity.name, startDate, endDate);
      setResult(data);
      if (onComplete) {
        onComplete(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await rptApi.downloadGLTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'gl_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download template: ' + err.message);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const isValid = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

      if (!isValid) {
        setError('Please select an Excel (.xlsx, .xls) or CSV file');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUploadGL = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await rptApi.uploadGL(entity.name, startDate, endDate, selectedFile);
      setResult(data);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (onComplete) {
        onComplete(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="panel fetch-gl-panel">
      <h2>Step 1: {isManualUploadEntity ? 'Select Date Range and Upload General Ledger' : 'Fetch General Ledger from SAP'}</h2>

      {isManualUploadEntity ? (
        <>
          <div className="alert alert-info" style={{ marginBottom: '20px' }}>
            <strong>No System Access Available</strong>
            <p>Select the reporting date range, download/fill the GL template, then upload the completed GL file here.</p>
          </div>
          <p className="panel-description">
            This upload creates a GL version for the selected period. Existing GL versions can be viewed or downloaded from the table below.
          </p>
        </>
      ) : (
        <p className="panel-description">
          Fetch the complete General Ledger data from SAP and store it on S3 for RPT analysis.
        </p>
      )}

      <div className="form-group">
        <label>{isManualUploadEntity ? 'Reporting Start Date:' : 'Start Date:'}</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          disabled={loading}
        />
      </div>

      <div className="form-group">
        <label>{isManualUploadEntity ? 'Reporting End Date:' : 'End Date:'}</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          disabled={loading}
        />
      </div>

      {isManualUploadEntity ? (
        <>
          <div className="form-group">
            <label>Download Template:</label>
            <button
              className="btn btn-secondary"
              onClick={handleDownloadTemplate}
              disabled={loading}
              style={{ marginBottom: '10px' }}
            >
              📥 Download GL Template
            </button>
            <p style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
              Download the Excel template, fill in your general ledger data, and upload it below.
            </p>
          </div>

          <div className="form-group">
            <label>Select Completed General Ledger File:</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              disabled={loading}
              style={{ marginBottom: '10px' }}
            />
            {selectedFile && (
              <p style={{ fontSize: '0.9em', color: '#28a745', marginTop: '5px' }}>
                ✓ Selected: {selectedFile.name}
              </p>
            )}
            <p style={{ fontSize: '0.85em', color: '#999', marginTop: '5px' }}>
              Accepts Excel (.xlsx, .xls) or CSV files
            </p>
          </div>

          <button
            className="btn btn-primary"
            onClick={handleUploadGL}
            disabled={loading || !startDate || !endDate || !selectedFile}
          >
            {loading ? 'Uploading...' : 'Upload GL for Selected Date Range'}
          </button>
        </>
      ) : (
        <button
          className="btn btn-primary"
          onClick={handleFetchGL}
          disabled={loading || !startDate || !endDate}
        >
          {loading ? 'Fetching GL Data...' : 'Fetch GL from SAP'}
        </button>
      )}

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {result && result.success && (
        <div className="alert alert-success">
          <h3>✓ {isManualUploadEntity ? 'Successfully Uploaded GL Data' : 'Successfully Fetched GL Data'}</h3>
          <div className="result-details">
            <p><strong>Entity:</strong> {result.entity_name}</p>
            <p><strong>Total GL Entries:</strong> {result.gl_summary?.total_entries?.toLocaleString() || result.summary?.total_entries?.toLocaleString()}</p>
            <p><strong>Unique Transactions:</strong> {result.gl_summary?.unique_transactions?.toLocaleString() || result.summary?.unique_transactions?.toLocaleString()}</p>
            <p><strong>Unique Accounts:</strong> {result.gl_summary?.unique_accounts?.toLocaleString() || result.summary?.unique_accounts?.toLocaleString()}</p>
            <p><strong>Period:</strong> {result.storage?.period}</p>
            <p><strong>Timestamp:</strong> {result.storage?.timestamp}</p>
            <p className="success-note">GL data stored successfully on S3. You can now proceed to Step 2.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default FetchGLPanel;
